/**
 * Access removed during this visit, each restorable in one click (ORDR-120,
 * FR-STF-010).
 *
 * WHY THE PAGE REMEMBERS AND THE SERVER DOES NOT. A revoke DELETEs the grant
 * row (`{"id", "revoked": true}`), so nothing on the server records what was
 * taken away. The obvious restore, "grant it again from the form", needs a user
 * id nobody on the floor has. So the panel keeps what IT revoked, in page
 * memory, and a restore POSTs the same five fields. That lasts until the page
 * is left, and the heading says so rather than implying more. A durable restore
 * needs a soft revoke in paio-auth.
 */

import type { ReactElement } from "react";
import { actionLabel } from "./rosterLabels.js";

/** What a restore needs, captured at the moment of the revoke. */
export interface RemovedGrant {
  /** The revoked grant's id: a key, never sent back. */
  readonly grantId: string;
  readonly user_id: string;
  readonly holderName: string;
  readonly roleName: string;
  readonly role_key: string;
  readonly scope_type: string;
  readonly scope_id: string;
}

export interface RemovedThisVisitProps {
  /** Already narrowed to the selected scope. */
  readonly removed: readonly RemovedGrant[];
  readonly ownUserId: string;
  readonly busy: boolean;
  readonly onRestore: (item: RemovedGrant) => void;
}

export function RemovedThisVisit({
  removed,
  ownUserId,
  busy,
  onRestore,
}: RemovedThisVisitProps): ReactElement | null {
  if (removed.length === 0) {
    return null;
  }
  return (
    <section data-testid="removed-list" className="mt-4 text-sm">
      <h3 className="font-medium">Removed this visit</h3>
      <p className="mb-2 text-xs">
        You can restore access removed during this visit until you leave this page.
      </p>
      <ul className="flex flex-col gap-1">
        {removed.map((item) => (
          <li key={item.grantId} className="flex items-center justify-between gap-2">
            <span>
              {item.holderName}: {item.roleName} at {item.scope_type}:{item.scope_id}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => onRestore(item)}
              data-testid={`restore-${item.grantId}`}
              className="rounded border border-[var(--color-neutral-300)] px-2 py-1 disabled:opacity-60"
            >
              {actionLabel("Restore", item.holderName, item.roleName, item.user_id === ownUserId)}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
